import React, {memo} from 'react'
import { useFormik } from 'formik'

import * as yup from 'yup'

import TextInput from 'components/CommonUI/TextInput/TextInput'
import Button from 'components/CommonUI/Button/Button'
import PhoneInputStyled from 'components/CommonUI/PhoneInput/PhoneInput'

import SelectTopic from './SelectTopic'
import SelectFacility from './SelectFacility'
import TextareaAutosize from '@material-ui/core/TextareaAutosize'
import { makeStyles } from '@material-ui/core/styles'
import {useShallowEqualSelector} from 'custom-hooks'
import {getIsAuthSelector, getProfileDataSelector, sendHelpSupportForm} from 'redux/reducers/profile'
import {useDispatch, useSelector} from 'react-redux'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
    },
  },
  textarea: {
    width: '515px',
    height: '600px',
    background: '#FFFFFF',
    border: '1px solid rgba(21, 31, 49, 0.15)',
    borderRadius: '6px',
    padding: '15px',
    resize: 'none',
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: '600px',
    fontSize: '16px',
    '&:focus': {
      outline: 'none',
      border: '1px solid #4AA652',
    },
    '&::placeholder': {
      fontWeight: '400',
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: '0.016em',
      color: 'rgba(21, 31, 49, 0.3)',
    },
  },
  error: {
    border: '1px solid #D40C20 !important',
    boxShadow: '0px 4px 6px rgba(212, 12, 32, 0.16)',
    width: '515px',
    height: '600px',
    background: '#FFFFFF',
    borderRadius: '6px',
    padding: '15px',
    resize: 'none',
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: '600px',
    fontSize: '16px',
    '&:focus': {
      outline: 'none',
      border: '1px solid #D40C20',
    },
  }
}))

let helpSupportSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim("First name can't start or end the space character")
    .strict()
    .matches(/^[a-zA-Z\s-']+$/, 'First name can only contain letters')
    .min(2, 'First name must be at least 2 characters')
    .max(256, 'First name must be at most 256 characters')
    .required('First name is a required'),
  lastName: yup
    .string()
    .trim("Last name can't start or end the space character")
    .strict()
    .matches(/^[a-zA-Z\s-']+$/, 'First name can only contain letters')
    .min(2, 'Last name must be at least 2 characters')
    .max(256, 'Last name must be at most 256 characters')
    .required('Last name is a required'),
  email: yup
      .string()
      .required('Email is required')
      .email('Invalid email address format'),
  phoneNumber: yup.number(),
  message: yup
      .string()
      .min(2, 'Comment must be at least 2 characters')
      .max(2000, 'Last name must be at most 2000 characters')
      .required('Comment is required')
})

const HelpSupportForm = () => {

  const dispatch = useDispatch()
  const classes = useStyles()
  const profile = useShallowEqualSelector(getProfileDataSelector)
  const isAuth = useSelector(getIsAuthSelector)

  const formik = useFormik({
    initialValues: {
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      email: profile.email || '',
      phoneNumber: profile.phone_number || '',
      topic: 'Consumption issue',
      location: 'All my facilities',
      message: '',
    },
    onSubmit: (values) => {
      const data = {
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          email: values.email,
          topic: values.topic,
          message: values.message,
          location_id: formik.values.location.id,
      }
      dispatch(sendHelpSupportForm(data))
      // console.log('values', values)
    },
    validationSchema: helpSupportSchema,
  })


  return (
    <form
      className="personal_information_section form_section"
      onSubmit={formik.handleSubmit}
    >
      <div className="form-row">
        <TextInput
          label="First name"
          classNameContainer="help-support-form-input name_input"
          placeholder="Simon"
          name="firstName"
          onChange={formik.handleChange}
          value={formik.values.firstName}
          errorMessage={formik.touched.firstName && formik.errors.firstName}
          onBlur={formik.handleBlur}
        />
        <TextInput
          label="Last name"
          classNameContainer="help-support-form-input name_input"
          placeholder="Pitt"
          name="lastName"
          onChange={formik.handleChange}
          value={formik.values.lastName}
          errorMessage={formik.touched.lastName && formik.errors.lastName}
          onBlur={formik.handleBlur}
        />
      </div>
      <div className="form-row">
        <TextInput
            label="Email"
            classNameContainer="help-support-form-input name_input email-input"
            placeholder="myemail@mail.com"
            name="email"
            type="email"
            onChange={formik.handleChange}
            value={formik.values.email}
            errorMessage={formik.touched.email && formik.errors.email}
            onBlur={formik.handleBlur}
        />
        <PhoneInputStyled
            label="Number"
            classNameContainer="help-support-form-input name-input-select"
            name="phoneNumber"
            onChange={formik.handleChange}
            value={formik.values.phoneNumber}
            styleMenu={{
              top: '40%',
              left: '51%',
            }}
        />
      </div>
      <div className="form-row">
        <SelectTopic
          name="topic"
          onChange={formik.setFieldValue}
          value={formik.values.topic}
        />

        {isAuth && (<div className="form-row">
          <SelectFacility
              name="location"
              onChange={formik.setFieldValue}
              value={formik.values.location}
          />
        </div>)}
      </div>

      <span className="form-textarea-label">Comment</span>
      <div className=" textarea_container">
        <TextareaAutosize
          aria-label="Comment"
          className={formik.touched.message && formik.errors.message ? (
              classes.error
          ) : (
              classes.textarea
          )}
          minRows={5}
          maxRows={5}
          placeholder="Type here..."
          name="message"
          onChange={formik.handleChange}
          value={formik.values.message}
          onBlur={formik.handleBlur}
        />
        {formik.touched.message && formik.errors.message  ? (
                       <div style={{ fontSize: 14, color: '#D40C20'}}>{formik.errors.message}</div>
                    ) : null}
      </div>
      <div className="button_container">
        <Button
            className="save_changed_button"
            type="submit"
            disabled={!(formik.isValid && formik.dirty)}
        >
          Send a report
        </Button>
      </div>
    </form>
  )
}

export default memo(HelpSupportForm)
